/**
 * Navigation structure, extracted from resources/views/components/links.blade.php.
 *
 * The Blade partial duplicated every link between a desktop (dropdown) and a
 * mobile (flat list) branch, which is how the two branches drifted apart. Here
 * the structure is declared once and each layout renders it differently.
 */

import type { Locale } from '@/i18n/locales';

export interface NavLink {
  /** Page key from the router registry, for internal links. */
  pageKey?: string;
  /** Absolute URL, for external links. */
  href?: string;
  /** Translation key for the label. */
  label: string;
  /** Extra class the stylesheets key off. */
  modifier: string;
  /** Locales this link is hidden in. */
  hiddenIn?: Locale[];
}

export interface NavGroup {
  /** Translation key for the dropdown label. */
  label: string;
  modifier: string;
  links: NavLink[];
}

/**
 * Pages with no Russian translation. The Blade template hid these from the
 * Russian navigation with `@if(app()->getLocale() !== 'ru')`.
 *
 * Note: the original guarded `sudameuuring` in the mobile branch but not in the
 * desktop one, so the Russian desktop menu linked to an untranslated page. That
 * looks unintended, so it is hidden in both layouts here.
 */
const ET_ONLY: Locale[] = ['ru'];

export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'generic.general_information',
    modifier: 'nav-item-uldinfo',
    links: [
      {
        pageKey: 'about',
        label: 'generic.about',
        modifier: 'nav-item-projektist',
      },
      {
        pageKey: 'for-donor',
        label: 'generic.for-donor',
        modifier: 'nav-item-geenidoonorile',
      },
      {
        href: 'https://genomics.ut.ee/et/sisu/eesti-geenivaramu-andmekaitsetingimused',
        label: 'generic.data-protection-conditions',
        modifier: 'nav-item-andmekaitsetingimused',
      },
      {
        pageKey: 'privacy-and-cookie-policy',
        label: 'generic.privacy_and_cookie_policy',
        modifier: 'nav-item-privaatsus-ja-küpsiste-poliitika',
      },
    ],
  },
  {
    label: 'generic.scientific_researches',
    modifier: 'nav-item-tervis',
    links: [
      {
        pageKey: 'sudameuuring',
        label: 'generic.sudameuuring',
        modifier: 'nav-item-tervis',
        hiddenIn: ET_ONLY,
      },
      {
        pageKey: 'personality-research',
        label: 'generic.personality_research',
        modifier: 'nav-item-tervis',
      },
      {
        pageKey: 'medications',
        label: 'generic.medications',
        modifier: 'nav-item-tervis',
      },
      {
        pageKey: 'mental-health-2026',
        label: 'generic.mental_health',
        modifier: 'nav-item-tervis',
        hiddenIn: ET_ONLY,
      },
      {
        pageKey: 'microbiome',
        label: 'generic.microbiome',
        modifier: 'nav-item-tervis',
        hiddenIn: ET_ONLY,
      },
      {
        pageKey: 'diabetes-study',
        label: 'generic.diabetes-study',
        modifier: 'nav-item-tervis',
        hiddenIn: ET_ONLY,
      },
    ],
  },
];

/** Top-level links rendered outside any dropdown. */
export const NAV_STANDALONE: NavLink[] = [
  {
    pageKey: 'contact',
    label: 'generic.contact',
    modifier: 'nav-item-kontakt',
  },
];

export function visibleIn(links: NavLink[], locale: Locale): NavLink[] {
  return links.filter((link) => !link.hiddenIn?.includes(locale));
}
